import type { DismissReason, RepBucket, SignalStatus } from '../../../shared/enums';
import type { FbnDetailRecord, FbnRowRecord, HDL_BROWSER_HOSTS, UPLOAD_CITIES } from '../../../shared/fields';
import type { PlanCheckRecord } from '../../../shared/fields';
import type { ImportResult, Signal, SignalList } from '../../../shared/types';
import { adjustSignalCount, signalCounts } from '../logic/signalCounts';
import { matchingLead, mergeSignalIntoLead, signalToLead } from '../logic/signals';
import { NetworkError, apiFetch } from './api';
import { getDb } from './db';
import { enqueue, listPending } from './outbox';
import { createLead, getSnapshot, updateLead } from './store';
import { syncAndSettle } from './sync';

/** New-business signals API client (D57). Online-backed like drafts (D33): the inbox lives on the
 *  server, where the daily collection writes it. */

export const listSignals = (status: SignalStatus | 'all' = 'new'): Promise<SignalList> =>
  apiFetch<SignalList>('GET', `/api/signals?status=${status}`);

export const setSignalStatus = (id: string, status: SignalStatus, leadId?: string): Promise<Signal> =>
  apiFetch<Signal>(
    'PATCH',
    `/api/signals/${encodeURIComponent(id)}`,
    leadId === undefined ? { status } : { status, lead_id: leadId },
  );

/** A triage queued for the sync (D81): the PATCH the server will get, in order with the lead it may
 *  name, so Dismiss and Add act at once offline and the server catches up. */
export const queueSignalPatch = async (
  id: string,
  body: { status: SignalStatus; lead_id?: string; reason?: DismissReason },
  label: string,
): Promise<void> => {
  await enqueue({ method: 'PATCH', path: `/api/signals/${encodeURIComponent(id)}`, body, label });
};

/** What the outbox still holds for each business (D81): the status its latest queued PATCH will set,
 *  so a list fetched before the flush does not bring back what the rep already decided. */
const pendingTriage = async (): Promise<Map<string, SignalStatus>> => {
  const latest = new Map<string, SignalStatus>();
  for (const op of await listPending()) {
    const id = op.method === 'PATCH' ? op.path.match(/^\/api\/signals\/([^/?]+)$/)?.[1] : undefined;
    const status = (op.body as { status?: SignalStatus } | undefined)?.status;
    if (id && status) latest.set(decodeURIComponent(id), status);
  }
  return latest;
};

/** Undo: the business back to new, queued after whatever took it away. */
export const restoreSignal = async (id: string, name: string): Promise<void> => {
  await queueSignalPatch(id, { status: 'new' }, `Bring back · ${name}`);
  await syncAndSettle();
};

/** A dismissal (D72, D81): as a chain it goes to the server at once, since the answer says what was
 *  learned and hides the other matches; any other goes through the outbox and acts at once. */
export const dismissSignal = async (
  id: string,
  reason?: DismissReason,
  name = id,
): Promise<(Signal & { learned?: string }) | { queued: true }> => {
  if (reason === 'chain')
    return apiFetch<Signal & { learned?: string }>('PATCH', `/api/signals/${encodeURIComponent(id)}`, {
      status: 'dismissed',
      reason,
    });
  await queueSignalPatch(
    id,
    reason ? { status: 'dismissed', reason } : { status: 'dismissed' },
    `Dismiss · ${name}`,
  );
  await syncAndSettle();
  return { queued: true };
};

/** The rep's own bucket (D72): hidden, for a month with 'later', shown for good — or null, back to the
 *  sort's own verdict (D75). */
export const setSignalBucket = (id: string, bucket: RepBucket | null, reason?: 'later'): Promise<Signal> =>
  apiFetch<Signal>(
    'PATCH',
    `/api/signals/${encodeURIComponent(id)}`,
    reason ? { bucket, reason } : { bucket },
  );

/** "Not a fit: a chain" without a dismissal, or "Not a chain" (D72). */
export const setChain = (id: string, chain: boolean): Promise<Signal & { learned?: string }> =>
  apiFetch<Signal & { learned?: string }>('PATCH', `/api/signals/${encodeURIComponent(id)}`, { chain });

/** Plan checks the rep's browser read on the OC Health portal ("Send to CRM", D58). */
export const importPlanChecks = (records: readonly PlanCheckRecord[]): Promise<ImportResult> =>
  apiFetch<ImportResult>('POST', '/api/signals/import', { source: 'ochca', records });

/** An HdL city's license search, read by the rep's browser with the Send to CRM bookmark (D62). */
export const importCityLicenses = (
  source: keyof typeof HDL_BROWSER_HOSTS,
  rows: readonly (readonly string[])[],
): Promise<ImportResult> => apiFetch<ImportResult>('POST', '/api/signals/import', { source, rows });

/** The county's filings the rep's browser read with the bookmark (D76): the rows of the days searched,
 *  for the file numbers worth opening… */
export const importFbnRows = (
  rows: readonly FbnRowRecord[],
  days: readonly string[],
): Promise<{ wanted: string[]; days: number; rows: number }> =>
  apiFetch('POST', '/api/signals/import', { source: 'fbn', days, rows });

/** …then those filings' pages, judged with Google and stored; the held ones wait for the daily re-ask. */
export const importFbnDetails = (
  rows: readonly FbnRowRecord[],
  days: readonly string[],
  details: readonly FbnDetailRecord[],
): Promise<ImportResult & { pending: number }> =>
  apiFetch(
    'POST',
    '/api/signals/import',
    { source: 'fbn', days, rows, details },
    { signal: AbortSignal.timeout(45_000) },
  );

/** "Run now" for one server source (D77): the server answers 202 and reads on; 409 within ten minutes of
 *  its last attempt or while it runs. */
export const runSourceNow = (source: string): Promise<{ started: boolean }> =>
  apiFetch<{ started: boolean }>('POST', `/api/signals/sources/${encodeURIComponent(source)}/run`);

/** A monthly city list the rep downloaded (Costa Mesa, Laguna Beach: D61), sent as it is. Reading a
 *  15-page PDF on the server takes a moment, so this waits longer than most calls. */
export const importCityFile = (city: (typeof UPLOAD_CITIES)[number], file: File): Promise<ImportResult> =>
  apiFetch<ImportResult>(
    'POST',
    `/api/signals/upload?${new URLSearchParams({ city, name: file.name.slice(0, 200) })}`,
    file,
    {
      signal: AbortSignal.timeout(45_000),
    },
  );

export interface AddResult {
  leadId: string;
  /** The rep already had this business as a lead; it was linked instead of duplicated. */
  existed: boolean;
  /** The inbox has the add, or will on the next sync (D81): it waits in the outbox behind the lead. */
  linked: boolean;
  /** The sync could not reach the server: the lead and the add are safe on the phone until it can. */
  offline: boolean;
}

/** "Add as lead": reuse a matching lead or create one (offline-first, D3), queue the inbox's add behind
 *  it (D81) and sync, so the server gets the lead first (D45) and the signal linked to it. */
export const addSignalAsLead = async (signal: Signal, today: string): Promise<AddResult> => {
  const existing = matchingLead(signal, (await getSnapshot()).leads);
  if (existing) {
    // the record's contact and details join the lead the rep already has (D58)
    const patch = mergeSignalIntoLead(existing, signal, today);
    if (patch) await updateLead(existing.id, patch);
  }
  const leadId = existing?.id ?? (await createLead(signalToLead(signal, today))).id;
  await queueSignalPatch(signal.id, { status: 'added', lead_id: leadId }, `Add · ${signal.business_name}`);
  const status = await syncAndSettle();
  return { leadId, existed: !!existing, linked: true, offline: status.state === 'offline' };
};

/** The inbox's new businesses kept on this device (D65), so the Overview, the sidebar, search and
 *  New businesses still show them offline. In the `meta` store: no schema change. */
const STORED_KEY = 'signals:new';

/** Fired on `window` when the stored list changes, so the sidebar count can follow. */
export const SIGNALS_CHANGED = 'sb:signals-changed';

/** The copy's shape; a copy from before the sort (D72) has no sections to show and is not used. */
export const STORED_VERSION = 2;

export interface StoredSignals {
  version: typeof STORED_VERSION;
  list: SignalList;
  /** When it was fetched (ISO date-time). */
  savedAt: string;
}

export const readStoredSignals = async (): Promise<StoredSignals | null> => {
  const row = await (await getDb()).get('meta', STORED_KEY);
  const stored = row?.value as Partial<StoredSignals> | undefined;
  return stored?.version === STORED_VERSION && stored.list ? (stored as StoredSignals) : null;
};

const keep = async (stored: StoredSignals): Promise<void> => {
  await (await getDb()).put('meta', { key: STORED_KEY, value: stored });
  window.dispatchEvent(new Event(SIGNALS_CHANGED));
};

/** Businesses triaged on this device, each with the tick it was triaged at: a fetch that started
 *  before a triage may still list the business, so its answer is not believed for it. */
let tick = 0;
const triaged = new Map<string, number>();

/** The stored copy's read-modify-writes, one after another: two triages settling in the same tick would
 *  each read the list with the other's row still in it, and the later write bring that row back. */
let chain: Promise<void> = Promise.resolve();
const serial = (f: () => Promise<void>): Promise<void> => (chain = chain.then(f, f));
/** Tests await the stored copy's pending writes instead of guessing a delay. */
export const _drainStoredSignalsForTests = (): Promise<void> => chain;

/** The new businesses to review: fetched and stored when online; the stored copy when the network
 *  is down. With no stored copy the network error goes to the caller, as before. */
export const loadNewSignals = async (): Promise<StoredSignals & { offline: boolean }> => {
  const started = tick;
  try {
    const fetched = await listSignals('new');
    const queued = await pendingTriage();
    // a row the rep triaged after this fetch began, or whose triage still waits in the outbox, is not
    // believed back from the server (D65, D81)
    const fresh = (s: Signal) => {
      const waiting = queued.get(s.id);
      return (triaged.get(s.id) ?? 0) <= started && waiting !== 'dismissed' && waiting !== 'added';
    };
    // a server from before the sort answers without the hidden rows, the counts and what each source
    // brought: the page stays up on what it has
    const counts = signalCounts(fetched);
    for (const s of [...fetched.signals, ...(fetched.hidden ?? [])])
      if (!fresh(s)) adjustSignalCount(counts, s, -1);
    const list: SignalList = {
      ...fetched,
      signals: fetched.signals.filter(fresh),
      hidden: (fetched.hidden ?? []).filter(fresh),
      counts,
      produced: fetched.produced ?? {},
    };
    // the server's answer now covers every triage made before this fetch began
    for (const [id, at] of triaged) if (at <= started) triaged.delete(id);
    const stored: StoredSignals = { version: STORED_VERSION, list, savedAt: new Date().toISOString() };
    await keep(stored);
    return { ...stored, offline: false };
  } catch (err) {
    const stored = err instanceof NetworkError ? await readStoredSignals() : null;
    if (!stored) throw err;
    return { ...stored, offline: true };
  }
};

/** A triaged business leaves the stored copy too, so an offline screen does not bring it back.
 *  Called once the server has the triage (D65). */
export const forgetStoredSignal = (id: string): Promise<void> => {
  triaged.set(id, ++tick);
  return serial(async () => {
    const stored = await readStoredSignals();
    if (!stored) return;
    await keep({ ...stored, list: withoutRow(stored.list, id) });
  });
};

type Counted = 'top' | 'queue' | 'hidden';
const counted = (bucket: string | undefined): bucket is Counted =>
  bucket === 'top' || bucket === 'queue' || bucket === 'hidden';

/** The list without one row, its bucket's count one lower. */
const withoutRow = (list: SignalList, id: string): SignalList => {
  const gone = [...list.signals, ...list.hidden].find((s) => s.id === id);
  const counts = signalCounts(list);
  if (counted(gone?.bucket)) counts[gone.bucket] = Math.max(0, counts[gone.bucket] - 1);
  return {
    ...list,
    signals: list.signals.filter((s) => s.id !== id),
    hidden: list.hidden.filter((s) => s.id !== id),
    counts,
  };
};

/** A row as the server now has it (shown, hidden, re-scored), in the stored copy until the next load. A
 *  PATCH answer carries no siblings or group size: the stored row's stay. */
export const updateStoredSignal = (fresh: Signal): Promise<void> =>
  serial(async () => {
    const stored = await readStoredSignals();
    if (!stored) return;
    const before = [...stored.list.signals, ...stored.list.hidden].find((x) => x.id === fresh.id);
    const s: Signal = {
      ...(before?.siblings ? { siblings: before.siblings } : {}),
      ...(before?.group_count ? { group_count: before.group_count } : {}),
      ...fresh,
    };
    const list = withoutRow(stored.list, s.id);
    const counts = signalCounts(list);
    if (counted(s.bucket)) counts[s.bucket] += 1;
    const byScore = (a: Signal, b: Signal) => b.score - a.score;
    const into = s.bucket === 'hidden' ? 'hidden' : 'signals';
    await keep({ ...stored, list: { ...list, [into]: [...list[into], s].sort(byScore), counts } });
  });
